import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import { subsetGetter, SubsetGetterProps, Subsets, subsetSetter } from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import cloneDeep from "lodash/cloneDeep";

export type Rontgen = {
    id: number;
    code: string;
    displayName: string;
    ordning: number;
};

export type RontgenDisplay = {
    value: Rontgen["id"];
    label: string;
};

type State = BaseState & {
    rontgen: Map<Rontgen["id"], Rontgen>;
};

export const convertRontgenData: (args: { rontgen?: Rontgen }) => RontgenDisplay | null = ({ rontgen }) => {
    if (!rontgen) {
        return null;
    }

    return {
        value: rontgen.id,
        label: rontgen.displayName,
    };
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        rontgen: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "rontgen";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        rontgen: (state: State) => [...state.rontgen.values()],
        rontgenSubset: (state: State) => (props: SubsetGetterProps<State> & { displayMode?: boolean }) => {
            const result = subsetGetter<State>({
                ...props,
                state,
                limit: 0,
                collectionName: moduleName,
            });

            if (props.displayMode && Array.isArray(result)) {
                return result.map((singleResult) => convertRontgenData({ rontgen: singleResult }));
            }

            return result;
        },
        rontgenById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }

                return state.rontgen.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addRontgen: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                rontgen = [],
            }: {
                resetSubset?: boolean;
                offset: number;
                subsetName: keyof Subsets;
                rontgen: Rontgen[];
            }
        ) =>
            subsetSetter<State, Rontgen>({
                offset,
                subsetName,
                state,
                resetSubset,
                collectionName: moduleName,
                items: rontgen,
            }),
    },
    actions: {
        ...baseActions({
            moduleName,
        }),
        fetchRontgen: async (
            { commit, state }: BaseActionsProps<State>,
            {
                displayMode,
                filterParams = {},
                config = {},
            }: {
                offset?: number;
                filterParams?: FilterParams;
                config?: {
                    tabbnamn?: string;
                };
                displayMode?: boolean;
            }
        ) => {
            try {
                const {
                    subsetName,
                    results,
                    total,
                    nextPage,
                    offset: resultOffset,
                } = await fetchItems<State, Rontgen>({
                    filterParams,
                    state,
                    config,
                    page: 1,
                    limit: 0,
                    collectionName: moduleName,
                    apiUrl: "operation/kodverk/rontgen/",
                });

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addRontgen", {
                    subsetName,
                    offset: resultOffset,
                    rontgen: results,
                });

                return {
                    total,
                    nextPage,
                    subsetName,
                    results: displayMode
                        ? results.map((singleResult) => convertRontgenData({ rontgen: singleResult }))
                        : results,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
